import { Injectable } from '@angular/core'
import { DepartmentService } from '@ftr/api-core'
import { LocationService } from '@ftr/data-location'
import { titleCase, unwrapData } from '@ftr/foundation'
import { FilterDescriptor, FilterKey, FilterStateModel, FilterType, FilterValueData } from '@ftr/lists'
import { UserGroupService } from '@ftr/ui-user'
import { DateTimeFormatter, LocalDate } from '@js-joda/core'
import { Locale } from '@js-joda/locale_en-us'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { firstValueFrom } from 'rxjs'
import { ClearReportFilterStateAction, SetReportFilterStateAction } from './report-filter.actions'
import {
  ReportFilterDateCountInterval,
  ReportFilterRelativeDateRangeKey,
  ReportFilterTypeMapping,
} from './report-filter.model'

export function defaultReportFilterState(): FilterStateModel {
  const defaultIntervalInDays = 27
  const dateTo = LocalDate.now()
  const dateFrom = dateTo.minusDays(defaultIntervalInDays)
  return {
    values: {
      [FilterKey.DateFrom]: dateFrom,
      [FilterKey.DateTo]: dateTo,
      [FilterKey.DateCountInterval]: 'd28', // Last 28 days,
    },
    labels: {
      [FilterType.Date]: {
        label: 'Last 28 days',
        subLabel: `${formatDate(dateFrom)} - ${formatDate(dateTo)}`,
      },
    },
  }
}

@State<FilterStateModel>({
  name: 'reportFilterState',
  defaults: defaultReportFilterState(),
})
@Injectable()
export class ReportFilterState {
  constructor(
    private readonly locationService: LocationService,
    private readonly departmentService: DepartmentService,
    private readonly userGroupService: UserGroupService,
  ) {}

  @Selector()
  static getFilterState(state: FilterStateModel): FilterStateModel {
    return state
  }

  @Action(ClearReportFilterStateAction)
  clearFilterState({ patchState }: StateContext<FilterStateModel>): void {
    patchState(defaultReportFilterState())
  }

  @Action(SetReportFilterStateAction)
  async setFilterValueState(
    { getState, patchState }: StateContext<FilterStateModel>,
    { filterValues, filterLabels }: SetReportFilterStateAction,
  ): Promise<void> {
    const currentState = getState()

    const newFilterValues = {
      ...currentState.values,
      ...filterValues,
    }

    const newFilterLabels = {
      ...currentState.labels,
      ...filterLabels,
    }

    // Filter label generation
    const reportFilterTypes = Object.values(FilterType)
    for (const filterType of reportFilterTypes) {
      if (!ReportFilterTypeMapping[filterType as FilterType]) {
        continue
      }
      // Skip generation if label is already available
      if (filterLabels[filterType]) {
        continue
      }
      // Loop over all filter keys of the filter type
      for (const filterKey of ReportFilterTypeMapping[filterType as FilterType]) {
        // Skip if filter value is undefined
        if (newFilterValues[filterKey as FilterKey] === undefined) {
          continue
        }

        const filterLabel = await this.generateFilterLabel(newFilterValues, filterKey)

        newFilterLabels[filterType as FilterType] = filterLabel
      }
    }

    patchState({
      values: newFilterValues as FilterValueData,
      labels: newFilterLabels,
    })
  }

  private async generateFilterLabel(
    filterValues: FilterValueData,
    filterKey: FilterKey,
  ): Promise<FilterDescriptor | undefined> {
    const fallbackFilterLabel = { label: 'Filter Selected' }
    let filterTypeMetadata: FilterDescriptor | undefined

    switch (filterKey) {
      case FilterKey.Courthouse:
        filterTypeMetadata = await this.generateLabelCourthouse(filterValues)
        break
      case FilterKey.Courtroom:
        filterTypeMetadata = await this.generateLabelCourtroom(filterValues)
        break
      case FilterKey.Department:
        filterTypeMetadata = await this.generateLabelDepartment(filterValues)
        break
      case FilterKey.UserGroup:
        filterTypeMetadata = await this.generateLabelUserGroup(filterValues)
        break
      case FilterKey.UserType:
        filterTypeMetadata = await this.generateLabelUser(filterValues)
        break
      case FilterKey.DateFrom:
      case FilterKey.DateTo:
      case FilterKey.DateCountInterval:
        filterTypeMetadata = this.generateLabelContentWithinDateRange(filterValues)
        break
      default:
        break
    }

    return filterTypeMetadata ?? fallbackFilterLabel
  }

  private async generateLabelCourthouse(filterValues: FilterValueData): Promise<FilterDescriptor> {
    try {
      const courthouseLocation = await firstValueFrom(
        this.locationService.get(filterValues[FilterKey.Courthouse] as string).pipe(unwrapData()),
      )
      return { label: courthouseLocation!.name }
    } catch {
      return { label: 'Courthouse Selected' } // Fallback
    }
  }

  private async generateLabelCourtroom(filterValues: FilterValueData): Promise<FilterDescriptor> {
    try {
      const courtroomLocation = await firstValueFrom(
        this.locationService.getCourtroom(filterValues[FilterKey.Courtroom] as string).pipe(unwrapData()),
      )
      return { label: courtroomLocation!.parent!.name, subLabel: courtroomLocation!.name }
    } catch {
      return { label: 'Courtroom Selected' } // Fallback
    }
  }

  private async generateLabelDepartment(filterValues: FilterValueData): Promise<FilterDescriptor> {
    try {
      const department = await firstValueFrom(
        this.departmentService.get(filterValues[FilterKey.Department] as string).pipe(unwrapData()),
      )
      return { label: department!.name }
    } catch {
      return { label: 'Department Selected' } // Fallback
    }
  }

  private async generateLabelUserGroup(filterValues: FilterValueData): Promise<FilterDescriptor> {
    try {
      const userGroup = await firstValueFrom(
        this.userGroupService.get(filterValues[FilterKey.UserGroup] as string).pipe(unwrapData()),
      )
      return { label: userGroup!.name }
    } catch {
      return { label: 'User Group Selected' } // Fallback
    }
  }

  private async generateLabelUser(filterValues: FilterValueData): Promise<FilterDescriptor> {
    return { label: `${titleCase((filterValues[FilterKey.UserType] as string) ?? 'all')} Users` }
  }

  private generateLabelContentWithinDateRange(filterValues: FilterValueData): FilterDescriptor {
    const rangeType = filterValues[FilterKey.DateCountInterval] as string
    if (rangeType === ReportFilterDateCountInterval.DayRange) {
      return { label: this.generateDateRangeLabel(filterValues) }
    }
    return { label: this.generateRelativeRangeLabel(filterValues), subLabel: this.generateDateRangeLabel(filterValues) }
  }

  private generateRelativeRangeLabel(filterValues: FilterValueData): string {
    const rangeKey = filterValues[FilterKey.DateCountInterval] as ReportFilterRelativeDateRangeKey
    switch (rangeKey) {
      case ReportFilterRelativeDateRangeKey.Yesterday:
        return 'Yesterday'
      case ReportFilterRelativeDateRangeKey.LastSevenDays:
        return 'Last 7 days'
      case ReportFilterRelativeDateRangeKey.LastTwentyEightDays:
        return 'Last 28 days'
      case ReportFilterRelativeDateRangeKey.ThisQuarter:
        return 'This quarter'
      case ReportFilterRelativeDateRangeKey.LastQuarter:
        return 'Last quarter'
      case ReportFilterRelativeDateRangeKey.YearToDate:
        return 'Year to date'
      case ReportFilterRelativeDateRangeKey.LastYear:
        return 'Last year'
      default:
        return this.generateDateRangeLabel(filterValues)
    }
  }

  private generateDateRangeLabel(filterValues: FilterValueData): string {
    const startDate = formatDate(filterValues[FilterKey.DateFrom] as LocalDate)
    const endDate = formatDate(filterValues[FilterKey.DateTo] as LocalDate)
    // To ensure the subLabel of "Yesterday" contains a single date
    if (startDate === endDate) {
      return startDate
    }
    return `${startDate} - ${endDate}`
  }
}

function formatDate(date: LocalDate): string {
  return date.format(DateTimeFormatter.ofPattern('d MMM, yyyy').withLocale(Locale.US))
}
